import React, { useId, useState } from 'react';

/**
 * Ordered list of strings with add / reorder / remove. One component serves
 * tags, related pages, key points and shareable quotes.
 *
 *   <ListField id="page-tags" label="Tags" items={page.tags} onChange={next => …}
 *              placeholder="recipes" chips validate={v => v.includes(' ') ? 'No spaces' : null} />
 *
 * `validate(value) -> string | null` runs before a duplicate check; both
 * failures render inline under the input, never in the page-level error.
 * `itemLink(value) -> path | null` adds an "Open" button that calls onNavigate.
 */
export default function ListField({
  id,
  label,
  items,
  onChange,
  placeholder,
  hint,
  multiline = false,
  validate,
  chips = false,
  emptyText = 'Nothing added yet.',
  itemLink,
  onNavigate,
  renderItem,
  addLabel = 'Add',
}) {
  const list = Array.isArray(items) ? items : [];
  const [adding, setAdding] = useState(false);
  const [input, setInput] = useState('');
  const [position, setPosition] = useState(list.length);
  const [error, setError] = useState(null);
  const reactId = useId();
  const baseId = id || `list-${reactId}`;
  const inputId = `${baseId}-input`;

  const reset = () => {
    setAdding(false);
    setInput('');
    setError(null);
  };

  const open = () => {
    setPosition(list.length);
    setAdding(true);
  };

  const add = () => {
    const value = input.trim();
    if (!value) return;
    const problem = validate ? validate(value) : null;
    if (problem) {
      setError(problem);
      return;
    }
    if (list.includes(value)) {
      setError('Already in the list');
      return;
    }
    const next = [...list];
    next.splice(Math.min(position, next.length), 0, value);
    onChange(next);
    reset();
  };

  const move = (index, delta) => {
    const to = index + delta;
    if (to < 0 || to >= list.length) return;
    const next = [...list];
    const [moved] = next.splice(index, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  const remove = (index) => onChange(list.filter((_, i) => i !== index));

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      reset();
    } else if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      add();
    }
  };

  const controls = (index) => (
    <span style={{ display: 'inline-flex', gap: 2, flexShrink: 0 }}>
      {itemLink && itemLink(list[index]) && (
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => onNavigate && onNavigate(itemLink(list[index]))}
          aria-label={`Open ${list[index]}`}
          title="Open"
        >
          ↗
        </button>
      )}
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => move(index, -1)} disabled={index === 0} aria-label={`Move ${list[index]} up`} title="Move up">↑</button>
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => move(index, 1)} disabled={index === list.length - 1} aria-label={`Move ${list[index]} down`} title="Move down">↓</button>
      <button type="button" className="btn btn-ghost btn-sm btn-danger-text" onClick={() => remove(index)} aria-label={`Remove ${list[index]}`} title="Remove">✕</button>
    </span>
  );

  return (
    <div className="field" id={baseId}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span className="field-label" style={{ margin: 0 }} id={`${baseId}-label`}>
          {label} <span style={{ fontWeight: 400, color: 'var(--color-muted)' }}>({list.length})</span>
        </span>
        {!adding && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={open} aria-describedby={`${baseId}-label`}>
            + {addLabel}
          </button>
        )}
      </div>

      {adding && (
        <div style={{ marginBottom: 10, padding: 10, background: 'var(--color-bg)', borderRadius: 'var(--radius)' }}>
          <label htmlFor={inputId} className="sr-only">{label}</label>
          {multiline ? (
            <textarea
              id={inputId}
              className={`textarea${error ? ' is-invalid' : ''}`}
              value={input}
              onChange={(e) => { setInput(e.target.value); setError(null); }}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              rows={3}
              autoFocus
              aria-invalid={!!error}
              aria-describedby={error ? `${inputId}-error` : undefined}
              style={{ minHeight: 60 }}
            />
          ) : (
            <input
              id={inputId}
              type="text"
              className={`input${error ? ' is-invalid' : ''}`}
              value={input}
              onChange={(e) => { setInput(e.target.value); setError(null); }}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              autoFocus
              aria-invalid={!!error}
              aria-describedby={error ? `${inputId}-error` : undefined}
            />
          )}
          {error && <span className="error" id={`${inputId}-error`} role="alert">{error}</span>}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
            {list.length > 0 && (
              <>
                <label htmlFor={`${baseId}-position`} className="sr-only">Position</label>
                <select
                  id={`${baseId}-position`}
                  className="select"
                  value={Math.min(position, list.length)}
                  onChange={(e) => setPosition(parseInt(e.target.value, 10))}
                  style={{ width: 'auto', padding: '5px 8px', fontSize: 13 }}
                >
                  {Array.from({ length: list.length + 1 }, (_, i) => (
                    <option key={i} value={i}>
                      {i === 0 ? 'At start' : i === list.length ? 'At end' : `Before #${i + 1}`}
                    </option>
                  ))}
                </select>
              </>
            )}
            <button type="button" className="btn btn-primary btn-sm" onClick={add} disabled={!input.trim()}>{addLabel}</button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={reset}>Cancel</button>
          </div>
        </div>
      )}

      {list.length === 0 ? (
        <span className="hint" style={{ marginTop: 0 }}>{emptyText}</span>
      ) : chips ? (
        <div className="chip-group" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {list.map((item, index) => (
            <span key={`${item}-${index}`} className="chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'default' }}>
              {renderItem ? renderItem(item) : item}
              {controls(index)}
            </span>
          ))}
        </div>
      ) : (
        <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {list.map((item, index) => (
            <li
              key={`${item}-${index}`}
              style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', fontSize: 13 }}
            >
              <span style={{ color: 'var(--color-muted)', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{index + 1}.</span>
              <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{renderItem ? renderItem(item) : item}</span>
              {controls(index)}
            </li>
          ))}
        </ol>
      )}
      {hint && <span className="hint">{hint}</span>}
    </div>
  );
}
